import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, Loader2, Heart, MessageCircle, User, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useSwipeToClose } from '../lib/useSwipeToClose';
import { formatDisplayDate } from '../lib/dateUtils';
import ReaderPanel from './ReaderPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthorPanel = ({ 
  authorId, 
  authorData: initialAuthorData, 
  isOpen, 
  onClose, 
  onSelectArticle,
  zIndexBase = 1060 
}) => {
  const [author, setAuthor] = useState(initialAuthorData || null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nestedArticle, setNestedArticle] = useState(null);
  const nestedArticleRef = useRef(null);

  // Keep ref in sync so popstate handler always sees latest value
  useEffect(() => {
    nestedArticleRef.current = nestedArticle;
  }, [nestedArticle]);

  // Sync initial author data if supplied
  useEffect(() => {
    if (initialAuthorData) {
      setAuthor(prev => (prev ? { ...prev, ...initialAuthorData } : initialAuthorData));
    }
  }, [initialAuthorData]);

  // Close nested article and pop Layer 3 back to Layer 2 if needed
  const handleCloseNestedArticle = useCallback(() => {
    setNestedArticle(null);
    if ((window.history.state?.layer || 0) >= 3) {
      window.history.back();
    }
  }, []);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' ? (window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches) : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768 || window.matchMedia('(max-width: 767px)').matches);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation lifecycle state
  const [shouldRender, setShouldRender] = useState(Boolean(isOpen && authorId));
  const [isExiting, setIsExiting] = useState(false);
  const [exitConfig, setExitConfig] = useState(null);
  const [hasEntered, setHasEntered] = useState(false);
  const isClosingRef = useRef(false);
  const exitTimerRef = useRef(null);
  const didPushHistoryRef = useRef(false);

  useEffect(() => {
    if (shouldRender) {
      const timer = setTimeout(() => {
        setHasEntered(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setHasEntered(false);
    }
  }, [shouldRender]);

  // Smooth dismiss handler: slides back to right edge
  const handleDismiss = useCallback((options = {}) => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    const fromSwipe = Boolean(options?.fromSwipe);
    const startOffset = options?.currentOffset || 0;
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
    const remainingDist = Math.max(0, screenWidth - startOffset);
    const duration = fromSwipe && startOffset > 0
      ? Math.max(130, Math.min(240, Math.round((remainingDist / screenWidth) * 260)))
      : 240;

    setExitConfig({ duration, fromSwipe, startOffset });
    setIsExiting(true);

    exitTimerRef.current = setTimeout(() => {
      setShouldRender(false);
      setIsExiting(false);
      setExitConfig(null);
      isClosingRef.current = false;
      exitTimerRef.current = null;

      // If dismissed via in-app swipe or button (not popstate), pop history back to layer 1
      if (didPushHistoryRef.current && !options?.fromPopState) {
        didPushHistoryRef.current = false;
        if ((window.history.state?.layer || 0) >= 2) {
          window.history.back();
        }
      }

      onClose();
    }, duration);
  }, [onClose]);

  // Swipe to close hook — disabled when a nested article reader is open
  const { handlers: swipeHandlers, swipeOffset, isSwiping, isSnappingBack } = useSwipeToClose(handleDismiss, shouldRender && !nestedArticle, isMobile);

  // Sync open state and intercept back button/gesture
  useEffect(() => {
    if (isOpen && authorId) {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      isClosingRef.current = false;
      setIsExiting(false);
      setExitConfig(null);
      setShouldRender(true);

      // Push history state layer 2 so Android back pops to layer 1
      if (window.history.state?.layer !== 2) {
        window.history.pushState({ authorPanel: true, layer: 2 }, '');
        didPushHistoryRef.current = true;
      }

      const handlePop = (event) => {
        const targetLayer = event.state?.layer || 0;

        // If nested article (Layer 3) was open and we popped back to Layer 2:
        if (nestedArticleRef.current && targetLayer <= 2) {
          setNestedArticle(null);
          return;
        }

        // If targetLayer is less than 2, AuthorPanel must close!
        if (targetLayer < 2) {
          didPushHistoryRef.current = false;
          handleDismiss({ fromPopState: true });
        }
      };
      window.addEventListener('popstate', handlePop);
      return () => {
        window.removeEventListener('popstate', handlePop);
      };
    } else if (!isOpen && shouldRender && !isClosingRef.current) {
      setIsExiting(true);
      setExitConfig(null);
      exitTimerRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
        setExitConfig(null);
        exitTimerRef.current = null;
      }, 240);
    }
  }, [isOpen, authorId, handleDismiss]);

  // Fetch author details & articles
  useEffect(() => {
    if (!shouldRender || !authorId) return;

    const fetchAuthorData = async () => {
      setLoading(true);
      try {
        const decoded = typeof authorId === 'string' ? decodeURIComponent(authorId) : String(authorId);
        const [authorRes, articlesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/authors/${encodeURIComponent(decoded)}`).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/authors/${encodeURIComponent(decoded)}/articles`).catch(() => ({ data: [] }))
        ]);

        if (authorRes?.data) {
          setAuthor(prev => ({ ...prev, ...authorRes.data }));
        } else if (!author) {
          setAuthor({
            id: decoded,
            name: decoded,
            role: 'Author',
            article_count: articlesRes?.data?.length || 0
          });
        }
        setArticles(articlesRes?.data || []);
      } catch (err) {
        console.error('Failed to load author details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthorData();
  }, [authorId, shouldRender]);

  if (!shouldRender || !authorId) return null;

  // Dynamic mobile transform styling matching ReaderPanel
  const getPanelStyle = () => {
    if (!isMobile) {
      return { transform: 'none' };
    }

    if (isSwiping) {
      return {
        transform: `translate3d(${Math.max(0, swipeOffset)}px, 0, 0)`,
        transition: 'none',
        willChange: 'transform',
        contain: 'paint',
      };
    }

    if (isSnappingBack) {
      return {
        transform: 'translate3d(0, 0, 0)',
        transition: 'transform 200ms cubic-bezier(0.25, 1, 0.5, 1)',
        willChange: 'transform',
        contain: 'paint',
      };
    }

    if (isExiting) {
      const exitDuration = exitConfig?.duration || 240;
      return {
        transform: 'translate3d(100%, 0, 0)',
        transition: `transform ${exitDuration}ms cubic-bezier(0.25, 1, 0.5, 1)`,
        willChange: 'transform',
        contain: 'paint',
      };
    }

    return {
      transform: 'translate3d(0, 0, 0)',
      contain: 'paint',
      willChange: 'transform',
    };
  };

  const getPanelAnimationClass = () => {
    if (!isMobile || isSwiping || isSnappingBack || isExiting || hasEntered) return '';
    return 'animate-mobile-panel-in';
  };

  const getBackdropStyle = () => {
    if (!isMobile) {
      return { opacity: 0.6 };
    }

    if (isSwiping) {
      const progress = Math.min(1, Math.max(0, swipeOffset / (window.innerWidth || 360)));
      return {
        opacity: Math.max(0, 0.6 * (1 - progress)),
        transition: 'none',
      };
    }

    if (isSnappingBack) {
      return {
        opacity: 0.6,
        transition: 'opacity 200ms ease-out',
      };
    }

    if (isExiting) {
      const exitDuration = exitConfig?.duration || 240;
      return {
        opacity: 0,
        transition: `opacity ${exitDuration}ms ease-out`,
        pointerEvents: 'none',
      };
    }

    return { opacity: 0.6 };
  };

  const getBackdropAnimationClass = () => {
    if (!isMobile || isSwiping || isSnappingBack || isExiting) return '';
    return 'animate-mobile-backdrop-in';
  };

  const authorName = author?.name || (typeof authorId === 'string' ? decodeURIComponent(authorId) : 'Author');
  const authorRole = author?.role === 'admin' ? 'Editor' : (author?.role || 'Author');

  const panelContent = (
    <>
      {/* Backdrop with dynamic z-index */}
      <div 
        className={`fixed inset-0 bg-black/60 ${swipeOffset > 0 && isSwiping ? 'pointer-events-none' : ''} ${getBackdropAnimationClass()}`}
        style={{
          ...getBackdropStyle(),
          zIndex: zIndexBase - 10,
          touchAction: 'none',
          overscrollBehavior: 'contain',
        }}
        onClick={() => handleDismiss()}
      />

      {/* Slide-out Panel with dynamic z-index */}
      <div 
        className={`fixed inset-y-0 right-0 w-full max-w-3xl author-panel-theme bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col overflow-hidden shadow-2xl ${getPanelAnimationClass()}`}
        style={{
          ...getPanelStyle(),
          zIndex: zIndexBase,
          height: isMobile ? '100dvh' : '100%',
          maxHeight: isMobile ? '100dvh' : '100%',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
        }}
        {...(isMobile ? swipeHandlers : {})}
      >
        {/* Top Header */}
        <div className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between px-3 sm:px-4 py-3 shadow-sm select-none">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <button
              onClick={() => handleDismiss()}
              className="p-1.5 -ml-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0 text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-700 transition-colors"
              aria-label="Back to article"
            >
              <ChevronLeft className="h-6 w-6 text-gray-800 dark:text-gray-200" />
            </button>
            <div className="w-8 h-8 rounded-full overflow-hidden bg-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-sm">
              {author?.profile_picture_url ? (
                <img src={author.profile_picture_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span>{authorName.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight">{authorName}</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-400 font-medium">Author Profile</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => handleDismiss()}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-700 dark:text-gray-200 active:bg-gray-200 dark:active:bg-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-950 p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-2xl mx-auto">
            {/* Author Profile Banner */}
            <div className="p-5 bg-gradient-to-r from-gray-50 to-gray-100/70 dark:from-gray-900 dark:to-gray-800/80 rounded-2xl border border-gray-200/80 dark:border-gray-800 mb-6 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left shadow-sm">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold flex items-center justify-center text-2xl border-2 border-white dark:border-gray-700 shadow-md flex-shrink-0">
                {author?.profile_picture_url ? (
                  <img src={author.profile_picture_url} alt={authorName} className="w-full h-full object-cover" />
                ) : (
                  <span>{authorName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-100/80 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full">
                    {authorRole}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    • {articles.length} {articles.length === 1 ? 'article' : 'articles'}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-snug mb-1">
                  {authorName}
                </h2>
                {author?.bio && (
                  <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mt-1">
                    {author.bio}
                  </p>
                )}
              </div>
            </div>

            {/* Articles List Heading */}
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Published Articles ({articles.length})
              </h3>
            </div>

            {/* Articles Feed */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-7 w-7 text-emerald-600 animate-spin mb-2" />
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Loading articles...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6">
                <User className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm font-bold text-gray-800 dark:text-white mb-1">No articles found</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">This author doesn&apos;t have any articles published yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {articles.map((art) => {
                  const formattedDate = formatDisplayDate(art.published_at || art.created_at || art.date);

                  return (
                    <div
                      key={art.id || art._id}
                      onClick={() => {
                        setNestedArticle(art);
                        window.history.pushState({ authorPanel: true, layer: 3, articleId: art.id }, '');
                      }}
                      className="group p-3.5 bg-white dark:bg-gray-900/90 rounded-xl border border-gray-200/80 dark:border-gray-800 hover:border-emerald-400 dark:hover:border-emerald-500/50 hover:shadow-sm transition-all cursor-pointer flex gap-3.5 items-center"
                    >
                      {art.image_url && (
                        <div className="w-20 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                          <img 
                            src={art.image_url} 
                            alt="" 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                            loading="lazy" 
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        {art.category && (
                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block mb-1">
                            {art.category.replace(/_/g, ' ')}
                          </span>
                        )}
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors line-clamp-2 leading-snug">
                          {art.title}
                        </h4>
                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                          <span>{formattedDate}</span>
                          {(() => {
                            const count = typeof art.reactions?.like_count === 'number'
                              ? art.reactions.like_count
                              : Array.isArray(art.likes)
                                ? art.likes.length
                                : typeof art.likes === 'number'
                                  ? art.likes
                                  : 0;
                            return count > 0 ? (
                              <span className="flex items-center gap-0.5 text-red-500">
                                <Heart className="h-2.5 w-2.5 fill-current" />
                                {count}
                              </span>
                            ) : null;
                          })()}
                          {art.comments_count ? (
                            <span className="flex items-center gap-0.5 text-gray-500 dark:text-gray-400">
                              <MessageCircle className="h-2.5 w-2.5" />
                              {art.comments_count}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nested Reader Panel for selected author article: slides smoothly in from right edge */}
      {nestedArticle && (
        <ReaderPanel
          article={nestedArticle}
          isOpen={Boolean(nestedArticle)}
          onClose={handleCloseNestedArticle}
          zIndexBase={Number(zIndexBase || 1060) + 40}
          nested={true}
        />
      )}
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(panelContent, document.body);
  }
  return panelContent;
};

export default AuthorPanel;
